import React, { useState } from 'react';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import { cn } from '@/utils/cn';
import { Button } from '@/components/ui/Button';
import { FEEDBACK_CONFIG, type FeedbackQuestion } from '@/config/feedback';

interface FeedbackCardProps {
  question: FeedbackQuestion;
  onSubmit: (response: string) => Promise<boolean>;
  isSubmitting: boolean;
  error: string | null;
  className?: string;
  /** Allow minimizing on mobile */
  allowMinimize?: boolean;
}

export const FeedbackCard: React.FC<FeedbackCardProps> = ({
  question,
  onSubmit,
  isSubmitting,
  error,
  className,
  allowMinimize = true,
}) => {
  const [selectedValue, setSelectedValue] = useState<string>('');
  const [freeformText, setFreeformText] = useState('');
  const [isMinimized, setIsMinimizedRaw] = useState(() => {
    return sessionStorage.getItem(FEEDBACK_CONFIG.STORAGE_KEYS.IS_MINIMIZED) === 'true';
  });

  const setIsMinimized = (minimized: boolean) => {
    setIsMinimizedRaw(minimized);
    if (minimized) {
      sessionStorage.setItem(FEEDBACK_CONFIG.STORAGE_KEYS.IS_MINIMIZED, 'true');
    } else {
      sessionStorage.removeItem(FEEDBACK_CONFIG.STORAGE_KEYS.IS_MINIMIZED);
    }
  };

  const handleSubmit = async () => {
    const response = question.type === 'freeform' ? freeformText : selectedValue;
    if (!response.trim()) return;

    const success = await onSubmit(response);
    if (success) {
      // Reset state after successful submission
      setSelectedValue('');
      setFreeformText('');
    }
  };

  const isSubmitDisabled =
    question.type === 'freeform' ? !freeformText.trim() : !selectedValue;

  // Minimized state - just show a small button to expand
  if (isMinimized && allowMinimize) {
    return (
      <button
        onClick={() => setIsMinimized(false)}
        className={cn(
          'bg-white rounded-lg shadow-lg border border-gray-200 p-3',
          'flex items-center gap-2 hover:bg-gray-50 transition-colors',
          'animate-feedback-appear',
          className
        )}
        aria-label="Expand feedback"
      >
        <span className="text-base" role="img" aria-label="feedback">
          💬
        </span>
        <span className="text-xs font-medium text-gray-700">Feedback</span>
        <ChevronUpIcon className="w-4 h-4 text-gray-400" />
      </button>
    );
  }

  return (
    <div
      className={cn(
        'bg-white rounded-lg shadow-lg border border-gray-200 p-4',
        'animate-feedback-appear',
        className
      )}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="text-base" role="img" aria-label="feedback">
            💬
          </span>
          <h3 className="text-xs font-semibold text-gray-900">Quick Feedback</h3>
        </div>
        {allowMinimize && (
          <button
            onClick={() => setIsMinimized(true)}
            className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
            aria-label="Minimize feedback"
          >
            <ChevronDownIcon className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Question */}
      <p className="text-xs text-gray-700 mb-3 leading-relaxed">{question.text}</p>

      {/* Response Input */}
      {question.type === 'rating' && (
        <RatingInput
          options={question.options || ['1', '2', '3', '4', '5']}
          value={selectedValue}
          onChange={setSelectedValue}
          disabled={isSubmitting}
          lowLabel={question.lowLabel}
          highLabel={question.highLabel}
        />
      )}

      {question.type === 'yes_no' && (
        <YesNoInput
          options={question.options || ['Yes', 'No', 'Neutral']}
          value={selectedValue}
          onChange={setSelectedValue}
          disabled={isSubmitting}
        />
      )}

      {question.type === 'freeform' && (
        <FreeformInput
          value={freeformText}
          onChange={setFreeformText}
          disabled={isSubmitting}
          placeholder="Share your thoughts..."
        />
      )}

      {/* Error Message */}
      {error && <p className="text-xs text-red-600 mt-2">{error}</p>}

      {/* Submit Button */}
      <div className="mt-3 flex justify-end">
        <Button
          variant="primary"
          size="sm"
          onClick={handleSubmit}
          disabled={isSubmitDisabled}
          loading={isSubmitting}
          className="text-xs px-3 py-1"
        >
          Submit
        </Button>
      </div>
    </div>
  );
};

// Rating Input (1-5)
interface RatingInputProps {
  options: string[];
  value: string;
  onChange: (value: string) => void;
  disabled: boolean;
  lowLabel?: string;
  highLabel?: string;
}

const RatingInput: React.FC<RatingInputProps> = ({
  options,
  value,
  onChange,
  disabled,
  lowLabel = 'Poor',
  highLabel = 'Excellent',
}) => (
  <div className="flex flex-col items-center gap-2">
    <div className="flex gap-1.5 justify-center">
      {options.map((option) => (
        <button
          key={option}
          onClick={() => onChange(option)}
          disabled={disabled}
          className={cn(
            'w-8 h-8 rounded-full border-2 text-xs font-medium transition-all',
            value === option
              ? 'border-blue-600 bg-blue-600 text-white'
              : 'border-gray-300 bg-white text-gray-700 hover:border-blue-400',
            disabled && 'opacity-50 cursor-not-allowed'
          )}
          aria-label={`Rate ${option} out of 5`}
          aria-pressed={value === option}
        >
          {option}
        </button>
      ))}
    </div>
    <div className="flex justify-between w-full text-xs text-gray-500 px-1">
      <span>{lowLabel}</span>
      <span>{highLabel}</span>
    </div>
  </div>
);

// Yes/No/Neutral Input
interface YesNoInputProps {
  options: string[];
  value: string;
  onChange: (value: string) => void;
  disabled: boolean;
}

const YesNoInput: React.FC<YesNoInputProps> = ({
  options,
  value,
  onChange,
  disabled,
}) => (
  <div className="flex gap-1.5 justify-center flex-wrap">
    {options.map((option) => (
      <button
        key={option}
        onClick={() => onChange(option)}
        disabled={disabled}
        className={cn(
          'px-3 py-1.5 rounded-md border text-xs font-medium transition-all',
          value === option
            ? 'border-blue-600 bg-blue-600 text-white'
            : 'border-gray-300 bg-white text-gray-700 hover:border-blue-400',
          disabled && 'opacity-50 cursor-not-allowed'
        )}
        aria-pressed={value === option}
      >
        {option}
      </button>
    ))}
  </div>
);

// Freeform Text Input
interface FreeformInputProps {
  value: string;
  onChange: (value: string) => void;
  disabled: boolean;
  placeholder?: string;
}

const FreeformInput: React.FC<FreeformInputProps> = ({
  value,
  onChange,
  disabled,
  placeholder,
}) => (
  <textarea
    value={value}
    onChange={(e) => onChange(e.target.value)}
    disabled={disabled}
    placeholder={placeholder}
    rows={3}
    className={cn(
      'w-full px-3 py-2 border border-gray-300 rounded-md text-sm',
      'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
      'resize-none',
      disabled && 'opacity-50 cursor-not-allowed bg-gray-50'
    )}
    maxLength={1000}
  />
);
